import { useAuthStore } from '@/stores/authStore';
import { useUserStore, USER_CURRENT_QUERY_KEY } from '@/stores/userStore';
import { Navigate } from '@tanstack/react-router';
import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserByToken } from '@/api/user';
import LoadingScreen from '../loadscreen';
import { supabase } from '@/lib/supabase';
import type { EmailOtpType } from '@supabase/supabase-js';

interface PrivateRouteProps {
  children: ReactNode;
}

export const AuthGuard = ({ children }: PrivateRouteProps) => {
  const { accessToken, setAccessToken } = useAuthStore((state) => state.auth);
  const { userData } = useUserStore((state) => state.user);

  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const appEnv = import.meta.env.VITE_APP_ENV;

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const tokenHash = searchParams.get('tokenHash') || searchParams.get('token_hash');
  const redirectTo = searchParams.get('redirect_to');
  const type = searchParams.get('type');
  const hashAccessToken = hashParams.get('access_token');

  useEffect(() => {
    async function handleSupabaseCallback() {
      if (hashAccessToken) {
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          setAccessToken(data.session.access_token);
          setIsAuthenticated(true);
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }
        setIsAuthChecking(false);
        return;
      }

      if (!tokenHash || !type) {
        setIsAuthChecking(false);
        return;
      }

      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as EmailOtpType,
      });

      if (!error && data?.session) {
        await supabase.auth.setSession(data.session);
        setAccessToken(data.session.access_token);
        setIsAuthenticated(true);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('token_hash');
        currentUrl.searchParams.delete('tokenHash');
        currentUrl.searchParams.delete('type');
        window.history.replaceState({}, '', currentUrl.pathname + currentUrl.search);
      }

      setIsAuthChecking(false);
    }

    handleSupabaseCallback();
  }, [tokenHash, redirectTo, type, hashAccessToken, setAccessToken]);

  const isLoggedIn = !!(accessToken || isAuthenticated);

  const userQuery = useQuery({
    queryKey: USER_CURRENT_QUERY_KEY,
    queryFn: getUserByToken,
    enabled: isLoggedIn && !isAuthChecking,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5 * 60 * 1000,
  });

  useLayoutEffect(() => {
    if (userQuery.data) {
      useUserStore.setState((state) => ({
        user: {
          ...state.user,
          userData: userQuery.data,
          activeTeam: userQuery.data.active_organization,
        },
      }));
    }
  }, [userQuery.data]);

  const isLoadingUser = isAuthChecking || (isLoggedIn && userQuery.isPending);

  const resolvedUser = userQuery.data ?? userData;

  if (isAuthChecking || isLoadingUser) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingScreen />
      </div>
    );
  }

  if (!accessToken && !isAuthenticated) {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;

    if ((currentPath === '/accept-organization-invite' || currentPath === '/set-password') && currentSearch) {
      const redirectUrl = `/sign-in?redirectTo=${encodeURIComponent(currentPath + currentSearch)}`;
      window.location.href = redirectUrl;
      return null;
    }

    return <Navigate to="/home" replace />;
  }

  if (
    window.location.pathname === '/setting-environment' ||
    window.location.pathname === '/create-organizations' ||
    window.location.pathname === '/set-password'
  ) {
    return <>{children}</>;
  }

  if (!resolvedUser && appEnv !== 'development') {
    if (redirectTo) {
      return <Navigate to={`/setting-environment?redirect_to=${redirectTo}` as unknown as string} replace />;
    } else {
      return <Navigate to="/setting-environment" replace />;
    }
  }

  if (resolvedUser && !resolvedUser.active_organization) {
    return <Navigate to="/create-organizations" replace />;
  }

  return <>{children}</>;
};
